import { createContext, createElement, useContext, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

/** Formats a year/month pair as `YYYY-MM-01`, the calendar-month period start the backend expects. */
function toPeriodStart(year: number, month: number): string {
  const mm = String(month + 1).padStart(2, "0");
  return `${String(year)}-${mm}-01`;
}

/** Today's period start (`YYYY-MM-01`), used when no `period` query parameter is present. */
export function currentPeriodStart(): string {
  const now = new Date();
  return toPeriodStart(now.getFullYear(), now.getMonth());
}

/** Returns whether a reporting period is the current calendar month. */
export function isCurrentPeriod(periodStart: string): boolean {
  return periodStart === currentPeriodStart();
}

/**
 * Keeps the selected period in app-local React state. The current month is deliberately restored
 * on a full page refresh; period selection is not part of application navigation or the URL.
 */
type SelectedPeriodContextValue = [string, Dispatch<SetStateAction<string>>];

const SelectedPeriodContext = createContext<SelectedPeriodContextValue | null>(null);

/** Provides the selected reporting period for the whole in-app shell. */
export function SelectedPeriodProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const periodState = useState(currentPeriodStart);
  return createElement(SelectedPeriodContext.Provider, { value: periodState }, children);
}

/** Reads the app-local reporting period. It resets to the current month on a full page refresh. */
export function useSelectedPeriod(): [string, (periodStart: string) => void] {
  const value = useContext(SelectedPeriodContext);
  if (!value) {
    throw new Error("useSelectedPeriod must be used within SelectedPeriodProvider");
  }

  return value;
}
